// 2) Third-party library imports
import { isEqual, uniqWith } from 'lodash-es';
import { createAction, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

// 3a) Constants
import { BANK_FILE_DESCRIPTORS_PAGE } from '../../config/tableAndPageConstants';
import { bankFileDescriptorsSortByOptions } from '../../constants/bankFileDescriptorsSortAndFilters';

// 3e) Redux actions & types
import {
    BankFileDescriptor,
    BankFileDescriptorsState,
    DeleteBankFileDescriptorRequestPayload,
    GetBankFileDescriptorsRequestPayload,
    RequestCreateOrEditBankFileDescriptorPayload,
} from './types';
import type { RootState } from '../storeTypes';
import { PageData } from '../common/types';

// Type-safe initialState!
export const initialState: BankFileDescriptorsState = {
    loading: false,
    data: [],
    pageData: {
        pageSize: BANK_FILE_DESCRIPTORS_PAGE.pageSize,
        currentPage: 0,
        hasNextPage: false,
    },
    saveBankFileDescriptorDataLoading: false,
    errorMessages: [],
    filters: {
        Customer: '',
        Descriptors: [],
        CompanyIds: undefined
    },
    sortBy: bankFileDescriptorsSortByOptions[1].value,
    sortAscending: false,
    tableFilter: undefined,
    activeData: {
        loading: false,
        selectedId: null,
        errorMessages: [],
        record: {}
    }
};

const bankFileDescriptorSlice = createSlice({
    name: 'bankFileDescriptor',
    initialState,
    reducers: {
        getBankFileDescriptorsRequestAction: (
            state,
            action: PayloadAction<GetBankFileDescriptorsRequestPayload>
        ) => {
            state.loading = true;
        },
        getBankFileDescriptorsSuccessAction: (
            state,
            action: PayloadAction<{
                data: BankFileDescriptor[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.data = action.payload.data;
            } else {
                const existingData = state.data || [];
                state.data = uniqWith([...existingData, ...action.payload.data], isEqual);
            }

            state.loading = false;
            state.pageData = action.payload.pageData;
            state.errorMessages = [];
        },
        getBankFileDescriptorsErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.loading = false;
            state.data = [];
            state.errorMessages = action.payload;
        },
        updateBankFileDescriptorsFiltersAction: (
            state,
            action: PayloadAction<GetBankFileDescriptorsRequestPayload['filters']>
        ) => {
           state.filters = action.payload
        },
        updateBankFileDescriptorsSortByAction: (
            state,
            action: PayloadAction<{
                sortBy: GetBankFileDescriptorsRequestPayload['sortBy'];
                sortAscending: boolean;
            }>
        ) => {
            state.sortBy = action.payload.sortBy;
            state.sortAscending = action.payload.sortAscending;
        },
        updateBankFileDescriptorsTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.tableFilter = action.payload;
        },
        clearBankFileDescriptorsStateAllTableFiltersAction: (state) => {
            state.filters = { ...initialState.filters };
            state.sortBy = initialState.sortBy;
            state.sortAscending = initialState.sortAscending;
            state.tableFilter = initialState.tableFilter;
        },
        setBankFileDescriptorSelectedIdRequestAction: {
            reducer: (
                state,
                action: PayloadAction<{
                    customerId: string | null;
                    callback: () => void;
                }>
            ) => {
                state.activeData.selectedId = initialState.activeData.selectedId;
            },
            prepare: (
                customerId: string | null,
                callback: () => void
            ) => ({
                payload: {
                    customerId,
                    callback
                }
            })
        },
        setBankFileDescriptorSelectedIdSuccessAction: (
            state,
            action: PayloadAction<string | null>
        ) => {
            state.activeData.selectedId = action.payload;
        },
        getBankFileDescriptorDataRequestAction: (
            state,
            _action: PayloadAction<string>
        ) => {
            state.activeData.record = { ...initialState.activeData.record };
            state.activeData.loading = true;
        },
        getBankFileDescriptorDataSuccessAction: (
            state,
            action: PayloadAction<{ record: {} }>
        ) => {
            Object.assign(state.activeData.record, action.payload.record);
            state.activeData.loading = false;
            state.activeData.errorMessages = [];
        },
        getBankFileDescriptorDataErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.record = { ...initialState.activeData.record };
            state.activeData.loading = false;
            state.activeData.errorMessages = action.payload;
        },
        clearBankFileDescriptorDataAction: (state) => {
            state.activeData = initialState.activeData;
        },
    }
});

const { actions, reducer } = bankFileDescriptorSlice;

// Actions
export const {
    getBankFileDescriptorsRequestAction,
    getBankFileDescriptorsSuccessAction,
    getBankFileDescriptorsErrorAction,
    updateBankFileDescriptorsFiltersAction,
    updateBankFileDescriptorsSortByAction,
    updateBankFileDescriptorsTableFilterAction,
    clearBankFileDescriptorsStateAllTableFiltersAction,
    setBankFileDescriptorSelectedIdRequestAction,
    setBankFileDescriptorSelectedIdSuccessAction,
    getBankFileDescriptorDataRequestAction,
    getBankFileDescriptorDataSuccessAction,
    getBankFileDescriptorDataErrorAction,
    clearBankFileDescriptorDataAction
} = actions;

export const clearBankFileDescriptorsListData = createAction(
    'bankFileDescriptor/clearBankFileDescriptorsListData'
);

export const createOrEditBankFileDescriptorRequestAction = createAction(
    'remittanceAdvice/createOrEditBankFileDescriptorRequestAction',
    function prepare(payload: RequestCreateOrEditBankFileDescriptorPayload) {
        return {
            payload
        };
    }
);

export const deleteBankFileDescriptorRequestAction = createAction(
    'remittanceAdvice/deleteBankFileDescriptorRequestAction',
    function prepare(payload: DeleteBankFileDescriptorRequestPayload) {
        return {
            payload
        };
    }
);

// Selectors
export const getBankFileDescriptorsState = (state: RootState) => state.bankFileDescriptors;
export const getBankFileDescriptorData = createSelector(
    getBankFileDescriptorsState,
    (bankFileDescriptors) => bankFileDescriptors.activeData
);
export const getBankFileDescriptorSelectedId = createSelector(
    getBankFileDescriptorData,
    (activeData) => activeData.selectedId
);

// Reducer
export default reducer;